import React, {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import { debounce } from 'lodash';
import {Editor} from '@tinymce/tinymce-react';
import 'tinymce/tinymce';
import 'tinymce/models/dom/model';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/skins/ui/oxide/skin';

import 'tinymce/plugins/link';
import 'tinymce/plugins/table';
import 'tinymce/plugins/preview';

import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';

import 'tinymce/plugins/emoticons/js/emojis';
import 'tinymce/skins/content/default/content';
import 'tinymce/skins/ui/oxide/content';

import i18next from "i18next";
import { renderHTML } from "@mnjsplatform/data/lib/utils/htmlUtils";
import {getMnHtmlEditorCustomization} from "./Customization/MnHtmlEditorCustomization";

const MnHtmlEditorInner = ({inline= false, ...props}) => {
    let isValueChanged = false;
    const editorRef = useRef(null);
    const dispatch = useDispatch();
    const max_chars = props.fieldAttributes && props.fieldInfo && props.fieldAttributes[props.fieldInfo.Name]
        ? props.fieldAttributes[props.fieldInfo.Name].max_chars
        : 4000;

    const elRef = (ref) => {
        const children = ref?.children;
        if (children && children[0]) {
            children[0].style.overflowX = "auto";
        }
    }

    const onSaveContent = (event, editor) => {
        event.content = event.content.replace(/&#39/g, "&apos");
    }
    const onEditorChange = (newValue, editor) => {
        //console.trace();
        isValueChanged = props.value !== newValue;
        if (props.onEditorChange) {
            props.onEditorChange(newValue);
        }
        if ((!max_chars || max_chars <= 0) ||
            editor.getContent({ format: 'text' }).length <= max_chars) {
            props.dataItem && props.fieldInfo
                ? dispatch(props.dataItem.setField(props.rowIndex, props.fieldInfo.Name, newValue))
                : Promise.resolve()
                    .then(() => {
                        if (newValue.length >= props.minSymbolsToAutoApply)
                            onEndEditFieldDebounced()
                    })
        }
    }
    const onEndEditField = () => {
        console.trace();
        if (props.onEndEditField && isValueChanged) {
            props.onEndEditField();
            isValueChanged = false;
        }
    }
    const onEndEditFieldDebounced = debounce(onEndEditField, props.debounceWait);
    const onPaste = (e, editor) => {
        let newContent = ((e.originalEvent || e).clipboardData || window.clipboardData).getData('Text');
        let oldContent = editor.getContent({format: 'text'});
        let length = newContent.length + oldContent.length;
        let isLimit = length >= props.max_chars;
        if (isLimit) {
            console.log('onPaste: prevent!');
            showNotification(i18next.t('HTMLEditor_1') + length + i18next.t('HTMLEditor_2'));
            e.preventDefault();
            e.stopImmediatePropagation();
            e.stopPropagation();
        }
    }
    const onKeyDown = (e, editor) => {
        //console.trace();
        let length = editor.getContent({ format: 'text' }).length;
        let isLimit = length >= max_chars;
        if (isLimit) {
            if ((e.keyCode > 47 && e.keyCode < 91) || (e.keyCode > 95 && e.keyCode < 112)) {
                console.log('onKeyDown: prevent!');
                e.preventDefault();
                e.stopImmediatePropagation();
                e.stopPropagation();
            }
        }
    }
    const onInit = (e, editor) => {
        if(editor.container && !editor.container.classList.contains('rounded-0')) {
            editor.container.classList.add('rounded-0');
        }

        const charsToText = (length, max_chars) => length + "/" + max_chars;
        const showNotification = (message) => {
            //editor.notificationManager.close();
            let notifications = editor.notificationManager.getNotifications();
            console.log(JSON.stringify(notifications));
            let oldNotifications = notifications.length > 0
                ? notifications.filter(n => n.settings.text === message)
                : null;
            let oldNotification = oldNotifications && oldNotifications.length > 0 ? oldNotifications[0] : null;
            if (!oldNotification) {
                editor.notificationManager.open({
                    text: message,
                    type: 'error',
                    timeout: 3000
                });
            }
        }
        const statusBar = editor.editorContainer && editor.editorContainer.querySelector('.tox-statusbar');
        const resizeBar = statusBar?.querySelector('.tox-statusbar__resize-handle');

        const update = function (e) {
            //console.trace();
            let label = resizeBar.querySelector('#charcount');
            let length = editor.getContent({format: 'text'}).length;
            label.innerHTML = charsToText(length, max_chars);
            let isLimit = length >= max_chars;
            if (isLimit) {
                if (!label.classList.contains('error')) {
                    label.classList.add('error');
                }
                showNotification(i18next.t('HTMLEditor_3') + length + i18next.t('HTMLEditor_4'));
            } else {
                if (label.classList.contains('error')) {
                    label.classList.remove('error');
                }
            }
        };
        const debouncedUpdate = debounce(update, 300);

        if (!max_chars || max_chars <= 0) return;

        if (statusBar) {
            const resizeBar = statusBar.querySelector('.tox-statusbar__resize-handle');
            if (resizeBar) {
                resizeBar.classList.add('ml-auto');
                tinymce.util.Delay.setEditorTimeout(editor, function () {
                    let length = editor.getContent({format: 'text'}).length;
                    const charCountPanel = document.createElement('span');
                    charCountPanel.id = 'charcount';
                    charCountPanel.classList.add('charcount', 'ml-auto');
                    if (length >= max_chars) {
                        charCountPanel.classList.add('error');
                    }
                    charCountPanel.textContent = charsToText(length, max_chars);
                    charCountPanel.disabled = props.readOnly;
                    resizeBar.insertBefore(charCountPanel, resizeBar.firstChild);
                    editor.on('setcontent beforeaddundo undo redo keyup', debouncedUpdate);
                }, 100);
            }
        }
    }
    if (props.readOnly) {
        let initValue = props.value;
        if (props.modificationRegExpArray) {
            for (let item of props.modificationRegExpArray) {
                initValue = props.value.replace(item.regExpValue, item.replaceValue);
            }
        }
        return (renderHTML(initValue, {overflowX: "auto"}, elRef));
    } else {
        return (
            <Editor
                inline={inline}
                licenseKey='gpl'
                value={props.value}
                statusbar={true}
                init={{
                    selector: 'textarea',
                    readonly: props.readOnly,
                    elementpath: false,
                    language: i18next.language === "en" ? "en_US" : i18next.language,
                    promotion: false,
                    branding: false,
                    resize: true,
                    //skin: false,
                    //menubar: false,
                    plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'preview'
                    ],
                    toolbar: 'undo redo | blocks | link | ' +
                        'bold italic forecolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'removeformat'
                }}
                onEditorChange={onEditorChange}
                onSaveContent={onSaveContent}
                onInit={onInit}
                onPaste={onPaste}
                onKeyDown={onKeyDown}
                onBlur={onEndEditField}
            />
        );
    }
}

const MnHtmlEditor = (props) => {
    const customProps = getMnHtmlEditorCustomization();

    useEffect(() => {
        console.log(i18next.language)
        import(`./tinymce/${i18next.language}`).catch(() => {
            // Fallback to default language if import fails
            import('./tinymce/ru');
        });
    }, []);

    return (
        <MnHtmlEditorInner {...props} {...customProps} />
    );
}

export default MnHtmlEditor;